import React, { useState, useRef, useLayoutEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Chip,
  Paper,
  Avatar,
  useMediaQuery,
} from '@mui/material';
import {
  AccountBalance,
  LocalHospital,
  Business,
  ShoppingCart,
  LocalShipping,
  School,
  Verified,
} from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import { useScrollReveal } from '../hooks/useScrollReveal';

/* ─── Per-industry accent colors (dark mode) ─── */
const INDUSTRY_COLORS = ['#0ea5e9', '#10b981', '#a855f7', '#f59e0b', '#f472b6', '#38bdf8'];

function IndustryFocus() {
  const { isLight } = useThemeMode();
  const isMobile = useMediaQuery('(max-width:768px)');
  const [activeTab, setActiveTab] = useState(0);
  const tabRefs = useRef([]);
  const indicatorRef = useRef(null);
  const { ref: revealRef, revealStyle } = useScrollReveal();

  const accentColor = isLight ? '#6366f1' : '#10b981';

  const industries = [
    {
      title: 'FinTech',
      icon: <AccountBalance />,
      color: INDUSTRY_COLORS[0],
      description: 'Transaction processing, compliance automation, and real-time fraud detection.',
      subSections: [
        { name: 'Digital Payments', desc: 'Real-time payment processing & settlement' },
        { name: 'Payment Gateways', desc: 'Multi-provider orchestration & failover' },
        { name: 'Trading Platforms', desc: 'Low-latency order routing & execution' },
        { name: 'Insurance Tech', desc: 'Claims automation & policy management' },
        { name: 'Banking APIs', desc: 'Open banking & account aggregation' },
      ],
      tags: ['Sub-second Latency', 'Bank-Grade Security', 'PCI Compliant', 'Automated KYC'],
    },
    {
      title: 'Healthcare',
      icon: <LocalHospital />,
      color: INDUSTRY_COLORS[1],
      description: 'HIPAA-compliant integration with automated patient workflows and EMR sync.',
      subSections: [
        { name: 'Hospital Systems', desc: 'EMR/EHR integration & patient data exchange' },
        { name: 'Telemedicine', desc: 'Virtual care platform orchestration' },
        { name: 'Medical Devices', desc: 'IoT device data ingestion & monitoring' },
        { name: 'Lab Systems', desc: 'Results processing & automated reporting' },
        { name: 'Pharmacy', desc: 'Prescription management & inventory sync' },
      ],
      tags: ['HIPAA Ready', 'HL7/FHIR', 'Real-time Sync', 'Audit Trails'],
    },
    {
      title: 'Enterprise',
      icon: <Business />,
      color: INDUSTRY_COLORS[2],
      description: 'Unified CRM-ERP systems, supply chain automation, and advanced analytics.',
      subSections: [
        { name: 'CRM Integration', desc: 'Salesforce, Dynamics 365 & HubSpot sync' },
        { name: 'ERP Systems', desc: 'SAP, Oracle & NetSuite orchestration' },
        { name: 'HR & Payroll', desc: 'Employee lifecycle automation' },
        { name: 'Business Intelligence', desc: 'Cross-system analytics & dashboards' },
        { name: 'Document Management', desc: 'Automated document workflows & archival' },
      ],
      tags: ['99.9% Uptime', 'SOC2 Compliant', 'SSO/SAML', 'Role-Based Access'],
    },
    {
      title: 'E-Commerce',
      icon: <ShoppingCart />,
      color: INDUSTRY_COLORS[3],
      description: 'Inventory sync, multi-channel order management, and fulfillment automation.',
      subSections: [
        { name: 'Order Management', desc: 'Multi-channel order routing & tracking' },
        { name: 'Inventory Sync', desc: 'Real-time stock across warehouses & channels' },
        { name: 'Marketplace Integration', desc: 'Amazon, Shopify, WooCommerce connectors' },
        { name: 'Returns & Refunds', desc: 'Automated RMA processing & credit' },
        { name: 'Customer Data', desc: 'Unified customer profiles & segmentation' },
      ],
      tags: ['Multi-Channel', 'Real-time Inventory', 'Auto-Scaling', 'Webhook-Ready'],
    },
    {
      title: 'Logistics',
      icon: <LocalShipping />,
      color: INDUSTRY_COLORS[4],
      description: 'Fleet tracking, route optimization, and end-to-end supply chain visibility.',
      subSections: [
        { name: 'Fleet Management', desc: 'GPS tracking & vehicle telemetry' },
        { name: 'Route Optimization', desc: 'AI-powered delivery planning' },
        { name: 'Warehouse Ops', desc: 'WMS integration & pick-pack automation' },
        { name: 'Carrier Integration', desc: 'Multi-carrier rate shopping & label generation' },
        { name: 'Shipment Tracking', desc: 'Real-time visibility across the supply chain' },
      ],
      tags: ['IoT Ready', 'EDI Support', 'Real-time Tracking', 'API-First'],
    },
    {
      title: 'EdTech',
      icon: <School />,
      color: INDUSTRY_COLORS[5],
      description: 'Learning platform integration, student data management, and content delivery.',
      subSections: [
        { name: 'LMS Integration', desc: 'Moodle, Canvas & Blackboard connectors' },
        { name: 'Student Information', desc: 'SIS sync & enrollment automation' },
        { name: 'Assessment Platforms', desc: 'Grade processing & analytics pipelines' },
        { name: 'Content Delivery', desc: 'Video streaming & resource distribution' },
        { name: 'Payment & Billing', desc: 'Tuition processing & subscription management' },
      ],
      tags: ['LTI Compliant', 'SCORM Support', 'Multi-Tenant', 'Data Privacy'],
    },
  ];

  const activeIndustry = industries[activeTab];
  const tabColor = isLight ? accentColor : activeIndustry.color;

  /* ─── Sliding indicator position tracking ─── */
  useLayoutEffect(() => {
    const el = tabRefs.current[activeTab];
    if (el && indicatorRef.current) {
      indicatorRef.current.style.top = `${el.offsetTop}px`;
      indicatorRef.current.style.height = `${el.offsetHeight}px`;
    }
  }, [activeTab]);

  /* ─── Shared section header ─── */
  const SectionHeader = ({ mb = 4 }) => (
    <Box sx={{ textAlign: 'center', mb }}>
      <Chip
        icon={<Verified sx={{ fontSize: '1rem' }} />}
        label="TRUSTED ACROSS INDUSTRIES"
        sx={{
          mb: 2, px: 2,
          background: isLight ? 'rgba(255,255,255,0.6)' : 'rgba(16,185,129,0.15)',
          border: isLight ? '1px solid rgba(255,255,255,0.8)' : '1px solid rgba(16,185,129,0.4)',
          borderRadius: '50px',
          color: isLight ? '#1e293b' : '#fff',
          fontSize: '0.75rem', fontWeight: 700, letterSpacing: '1px',
          '& .MuiChip-icon': { color: accentColor },
          position: 'relative',
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0, left: '-75%', width: '50%', height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)',
            transform: 'skewX(-20deg)',
            animation: 'shine-sweep 4s ease-in-out infinite',
            animationDelay: '0.5s',
          },
        }}
      />
      <Typography variant="h2" component="h2" sx={{
        fontSize: { xs: '1.8rem', sm: '2.2rem', md: '3rem' }, fontWeight: 900, lineHeight: 1.1, mb: 1.5,
        color: isLight ? '#0f172a' : '#fff',
      }}>
        Enterprise Integration{' '}
        <Box component="span" sx={{
          background: isLight
            ? 'linear-gradient(135deg, #4f46e5 0%, #6366f1 50%, #818cf8 100%)'
            : 'linear-gradient(135deg, #38bdf8, #8b5cf6)',
          backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>Your Industry Needs</Box>
      </Typography>
      <Typography variant="body1" sx={{
        fontSize: { xs: '0.95rem', md: '1.05rem' },
        color: isLight ? 'rgba(15,23,42,0.65)' : 'rgba(248,250,252,0.65)',
        maxWidth: 620, mx: 'auto',
      }}>
        Purpose-built middleware and backend orchestration for regulated, high-throughput industries.
      </Typography>
    </Box>
  );

  /* ─── Mobile: vertical card stack with accordion feel ─── */
  if (isMobile) {
    return (
      <Box component="section" id="industries" sx={{
        py: 6, position: 'relative',
        background: isLight ? 'transparent' : 'linear-gradient(45deg, #020617 0%, #0f172a 30%, #1e293b 60%, #334155 70%, #0f172a 90%, #020617 100%)',
      }}>
        <Container maxWidth="lg">
          <SectionHeader />
          {industries.map((ind, i) => (
            <MobileIndustryCard key={i} ind={ind} index={i} isLight={isLight} accentColor={accentColor} />
          ))}
        </Container>
      </Box>
    );
  }

  /* ─── Desktop: vertical tabs left + content right ─── */
  return (
    <Box
      component="section"
      id="industries"
      sx={{
        py: { xs: 6, md: 10 },
        position: 'relative',
        overflow: 'hidden',
        background: isLight
          ? 'transparent'
          : 'linear-gradient(35deg, #020617 0%, #0f172a 30%, #1e293b 60%, #334155 70%, #0f172a 90%, #020617 100%)',
        backdropFilter: isLight ? 'blur(20px)' : undefined,
        WebkitBackdropFilter: isLight ? 'blur(20px)' : undefined,
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }} ref={revealRef}>
        <Box sx={{ ...revealStyle(0) }}>
          <SectionHeader mb={{ xs: 4, md: 5 }} />
        </Box>

        {/* Tabs + Content layout */}
        <Box sx={{
          display: 'flex',
          gap: { md: 4, lg: 5 },
          alignItems: 'flex-start',
          ...revealStyle(150),
        }}>

          {/* ── Left: Vertical tab rail ── */}
          <Box sx={{
            position: 'relative',
            flexShrink: 0,
            width: { md: 200, lg: 220 },
          }}>
            {/* Sliding active indicator */}
            <Box
              ref={indicatorRef}
              sx={{
                position: 'absolute',
                left: 0, width: 3, borderRadius: 2,
                background: tabColor,
                transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: `0 0 12px ${tabColor}60`,
              }}
            />

            {industries.map((ind, i) => {
              const isActive = activeTab === i;
              const itemColor = isLight ? accentColor : ind.color;
              return (
                <Box
                  key={i}
                  ref={el => tabRefs.current[i] = el}
                  onClick={() => setActiveTab(i)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    px: 2, py: 1.8,
                    cursor: 'pointer',
                    borderRadius: '0 12px 12px 0',
                    transition: 'all 0.3s ease',
                    background: isActive
                      ? isLight ? 'rgba(99, 102, 241, 0.06)' : `${itemColor}10`
                      : 'transparent',
                    '&:hover': {
                      background: isActive
                        ? isLight ? 'rgba(99, 102, 241, 0.08)' : `${itemColor}15`
                        : isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)',
                      '& .tab-icon': {
                        transform: 'scale(1.15) rotate(-5deg)',
                      },
                    },
                  }}
                >
                  <Avatar
                    className="tab-icon"
                    sx={{
                      width: 36, height: 36,
                      background: isActive
                        ? isLight ? 'rgba(99, 102, 241, 0.12)' : `${itemColor}20`
                        : isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)',
                      color: isActive ? itemColor : (isLight ? 'rgba(15,23,42,0.4)' : 'rgba(248,250,252,0.4)'),
                      transition: 'all 0.3s ease, transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    }}
                  >
                    {React.cloneElement(ind.icon, { sx: { fontSize: '1.1rem' } })}
                  </Avatar>
                  <Typography sx={{
                    fontWeight: isActive ? 700 : 500,
                    fontSize: '0.9rem',
                    color: isActive
                      ? (isLight ? '#0f172a' : '#f1f5f9')
                      : (isLight ? 'rgba(15,23,42,0.5)' : 'rgba(248,250,252,0.45)'),
                    transition: 'all 0.3s ease',
                    letterSpacing: isActive ? '0.3px' : '0px',
                  }}>
                    {ind.title}
                  </Typography>

                  {/* Active dot pulse */}
                  {isActive && (
                    <Box sx={{
                      ml: 'auto',
                      width: 6, height: 6, borderRadius: '50%',
                      background: itemColor,
                      boxShadow: `0 0 8px ${itemColor}80`,
                      animation: 'industryPulse 2s ease-in-out infinite',
                      '@keyframes industryPulse': {
                        '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                        '50%': { opacity: 0.5, transform: 'scale(0.7)' },
                      },
                    }} />
                  )}
                </Box>
              );
            })}

            {/* Tab count badge */}
            <Box sx={{
              mt: 2, px: 2,
              display: 'flex', alignItems: 'center', gap: 1,
            }}>
              <Typography sx={{
                fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.5px',
                color: isLight ? 'rgba(15,23,42,0.35)' : 'rgba(248,250,252,0.3)',
                textTransform: 'uppercase',
              }}>
                {activeTab + 1} / {industries.length} Industries
              </Typography>
            </Box>
          </Box>

          {/* ── Right: Content panel ── */}
          <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, x: 20, scale: 0.98 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: -15, scale: 0.98 }}
                transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
              >
                <IndustryPanel ind={activeIndustry} isLight={isLight} accentColor={accentColor} tabColor={tabColor} />
              </motion.div>
            </AnimatePresence>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

/* ── Desktop content panel ── */
function IndustryPanel({ ind, isLight, accentColor, tabColor }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { md: 3.5, lg: 4 },
        borderRadius: 4,
        background: isLight ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.5)',
        border: isLight ? '1px solid rgba(0,0,0,0.06)' : `1px solid ${tabColor}20`,
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        position: 'relative',
        overflow: 'hidden',
        transition: 'border-color 0.4s ease',
        /* Subtle corner glow */
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -60, right: -60,
          width: 200, height: 200,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${tabColor}12 0%, transparent 70%)`,
          pointerEvents: 'none',
          transition: 'background 0.4s ease',
        },
      }}
    >
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5, position: 'relative' }}>
        <motion.div
          initial={{ scale: 0.6, rotate: -15 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        >
          <Avatar sx={{
            width: 52, height: 52,
            background: isLight ? 'rgba(99, 102, 241, 0.10)' : `${tabColor}18`,
            color: tabColor,
            transition: 'all 0.3s ease',
          }}>
            {React.cloneElement(ind.icon, { sx: { fontSize: '1.6rem' } })}
          </Avatar>
        </motion.div>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" component="h3" sx={{
            fontWeight: 800, color: isLight ? '#0f172a' : '#fff',
            fontSize: { md: '1.4rem', lg: '1.6rem' },
            lineHeight: 1.2,
          }}>
            {ind.title}
          </Typography>
          <Typography variant="body2" sx={{
            color: isLight ? 'rgba(15,23,42,0.6)' : 'rgba(248,250,252,0.6)',
            fontSize: '0.9rem', mt: 0.3,
          }}>
            {ind.description}
          </Typography>
        </Box>
      </Box>

      {/* Sub-sections grid — staggered entrance */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { md: '1fr 1fr', lg: '1fr 1fr 1fr' },
        gap: 1.5, mb: 2.5,
      }}>
        {ind.subSections.map((sub, idx) => (
          <motion.div
            key={`${ind.title}-${idx}`}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 + idx * 0.06, duration: 0.3, ease: 'easeOut' }}
          >
            <Box sx={{
              display: 'flex', alignItems: 'flex-start', gap: 1.5,
              p: 1.5, borderRadius: 2,
              background: isLight ? 'rgba(255,255,255,0.6)' : 'rgba(15,23,42,0.5)',
              border: isLight ? '1px solid rgba(0,0,0,0.04)' : `1px solid ${tabColor}10`,
              transition: 'all 0.25s ease',
              cursor: 'default',
              '&:hover': {
                background: isLight ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.7)',
                borderColor: isLight ? 'rgba(99,102,241,0.15)' : `${tabColor}25`,
                transform: 'translateX(4px)',
                '& .sub-dot': {
                  transform: 'scale(1.6)',
                  boxShadow: `0 0 6px ${tabColor}60`,
                },
              },
            }}>
              <Box className="sub-dot" sx={{
                width: 5, height: 5, borderRadius: '50%',
                background: tabColor,
                mt: 0.7, flexShrink: 0, opacity: 0.8,
                transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              }} />
              <Box>
                <Typography sx={{
                  fontWeight: 700, fontSize: '0.8rem',
                  color: isLight ? '#0f172a' : '#f1f5f9',
                  lineHeight: 1.3, mb: 0.2,
                }}>
                  {sub.name}
                </Typography>
                <Typography sx={{
                  fontSize: '0.72rem',
                  color: isLight ? 'rgba(15,23,42,0.55)' : 'rgba(248,250,252,0.5)',
                  lineHeight: 1.4,
                }}>
                  {sub.desc}
                </Typography>
              </Box>
            </Box>
          </motion.div>
        ))}
      </Box>

      {/* Tags — staggered pop-in */}
      <Box sx={{
        display: 'flex', flexWrap: 'wrap', gap: 0.75,
        pt: 2, borderTop: isLight ? '1px solid rgba(0,0,0,0.05)' : `1px solid ${tabColor}12`,
      }}>
        {ind.tags.map((tag, idx) => (
          <motion.div
            key={`${ind.title}-tag-${idx}`}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 + idx * 0.05, duration: 0.25, type: 'spring', stiffness: 400, damping: 25 }}
          >
            <Chip label={tag} size="small" sx={{
              background: isLight ? 'rgba(99, 102, 241, 0.08)' : `${tabColor}12`,
              color: isLight ? '#4f46e5' : tabColor,
              fontWeight: 600, fontSize: '0.72rem',
              border: isLight ? '1px solid rgba(99, 102, 241, 0.2)' : `1px solid ${tabColor}25`,
              height: 26,
              transition: 'all 0.2s ease',
              '& .MuiChip-label': { px: 1.5 },
              '&:hover': {
                background: isLight ? 'rgba(99, 102, 241, 0.14)' : `${tabColor}20`,
                transform: 'translateY(-1px)',
              },
            }} />
          </motion.div>
        ))}
      </Box>
    </Paper>
  );
}

/* ── Mobile: collapsible card (tap to expand) ── */
function MobileIndustryCard({ ind, index, isLight, accentColor }) {
  const [expanded, setExpanded] = useState(index === 0);
  const itemColor = isLight ? accentColor : ind.color;

  return (
    <Paper
      elevation={0}
      onClick={() => setExpanded(prev => !prev)}
      sx={{
        p: 2.5,
        mb: 1.5,
        borderRadius: 3,
        background: isLight ? 'rgba(255,255,255,0.65)' : 'rgba(15,23,42,0.5)',
        border: expanded
          ? isLight ? '1px solid rgba(99,102,241,0.2)' : `1px solid ${itemColor}30`
          : isLight ? '1px solid rgba(0,0,0,0.06)' : '1px solid rgba(255,255,255,0.06)',
        backdropFilter: 'blur(24px)',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
      }}
    >
      {/* Always-visible header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{
          width: 40, height: 40,
          background: expanded
            ? isLight ? 'rgba(99,102,241,0.12)' : `${itemColor}20`
            : isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)',
          color: expanded ? itemColor : (isLight ? 'rgba(15,23,42,0.4)' : 'rgba(248,250,252,0.4)'),
          transition: 'all 0.3s ease',
        }}>
          {React.cloneElement(ind.icon, { sx: { fontSize: '1.2rem' } })}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{
            fontWeight: 700, fontSize: '1rem',
            color: isLight ? '#0f172a' : '#fff',
          }}>
            {ind.title}
          </Typography>
          <Typography sx={{
            fontSize: '0.78rem',
            color: isLight ? 'rgba(15,23,42,0.55)' : 'rgba(248,250,252,0.55)',
            lineHeight: 1.3,
          }}>
            {ind.description}
          </Typography>
        </Box>
        <Box sx={{
          width: 24, height: 24,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: '50%',
          background: expanded ? `${itemColor}15` : 'transparent',
          transition: 'all 0.3s ease',
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          color: isLight ? 'rgba(15,23,42,0.4)' : 'rgba(248,250,252,0.4)',
          fontSize: '0.9rem', fontWeight: 700,
          flexShrink: 0,
        }}>
          ▾
        </Box>
      </Box>

      {/* Expandable content */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            style={{ overflow: 'hidden' }}
          >
            <Box sx={{ mt: 2 }}>
              {/* Sub-sections */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1, mb: 1.5 }}>
                {ind.subSections.map((sub, idx) => (
                  <Box key={idx} sx={{
                    display: 'flex', alignItems: 'flex-start', gap: 1.5,
                    p: 1.5, borderRadius: 2,
                    background: isLight ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)',
                    border: isLight ? '1px solid rgba(0,0,0,0.04)' : `1px solid ${itemColor}10`,
                  }}>
                    <Box sx={{
                      width: 5, height: 5, borderRadius: '50%',
                      background: itemColor, mt: 0.7, flexShrink: 0, opacity: 0.8,
                    }} />
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: isLight ? '#0f172a' : '#f1f5f9', lineHeight: 1.3, mb: 0.2 }}>
                        {sub.name}
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: isLight ? 'rgba(15,23,42,0.55)' : 'rgba(248,250,252,0.5)', lineHeight: 1.4 }}>
                        {sub.desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Tags */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, pt: 1.5, borderTop: isLight ? '1px solid rgba(0,0,0,0.04)' : `1px solid ${itemColor}10` }}>
                {ind.tags.map((tag, idx) => (
                  <Chip key={idx} label={tag} size="small" sx={{
                    background: isLight ? 'rgba(99,102,241,0.08)' : `${itemColor}12`,
                    color: isLight ? '#4f46e5' : itemColor, fontWeight: 600, fontSize: '0.72rem',
                    border: isLight ? '1px solid rgba(99,102,241,0.2)' : `1px solid ${itemColor}25`, height: 26,
                    '& .MuiChip-label': { px: 1.5 },
                  }} />
                ))}
              </Box>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Paper>
  );
}

export default IndustryFocus;